import type { Placement } from 'react-overlays/usePopper';
import type { ActionType, OverlayDirectionEnum, OverlayAlignEnum } from './Popover.types';

/**
 * Handles the interaction logic for showing or hiding the popover based on the action type.
 * @param action - The action type ('click' or 'hover') that triggers the popover.
 * @param show - The current visibility state of the popover.
 * @param setShow - The function to update the visibility state of the popover.
 * @param actionType - The type of action currently being performed.
 * @param value - Optional boolean value indicating the hover state (true for hover in, false for hover out).
 * @param disabled - Optional boolean value indicating the disabled state of target component.
 */
export const handleInteraction = (
  action: ActionType | undefined,
  show: boolean,
  setShow: React.Dispatch<React.SetStateAction<boolean>>,
  actionType: ActionType,
  value?: boolean,
  disabled?: boolean,
) => {
  if (action !== actionType || disabled) {
    return;
  }

  if (action === 'Click' || action === 'DoubleClick') {
    setShow(!show);
  } else if (action === 'Hover') {
    setShow(value || false);
  }

};

/**
 * Determines the placement of the popover based on direction and alignment.
 * @param direction - The direction of the popover ('top', 'bottom', 'right', 'left').
 * @param alignment - The alignment of the popover ('start', 'end', 'center').
 * @returns The calculated placement string for the popover.
 */
export const getPlacement = (
  alignment: OverlayAlignEnum = 'Center', // Default alignment to 'center'
  direction: OverlayDirectionEnum = 'Down', // Default direction to 'down'
): Placement => {
  const basePlacement = direction === 'Up' ? 'top' : direction === 'Down' ? 'bottom' : direction.toLowerCase();
  return alignment === 'Center' ? basePlacement as Placement : `${basePlacement}-${alignment.toLowerCase()}` as Placement;
};

/**
 * Gets the width value based on the provided parameters.
 * If `matchTriggerWidth` is `true` and `width` is `'match-trigger'`,
 * it returns the width of the `targetRef` element.
 *
 * @param {string | number | undefined} width - Width value or 'match-trigger'.
 * @param {React.RefObject<HTMLDivElement> | undefined} targetRef - Optional reference to the target element.
 * @param {boolean} matchTriggerWidth - Flag indicating whether to match width with `targetRef`.
 * @returns {string | number | undefined} Calculated width value.
 */
export const getWidth = (
  width?: string | number,
  targetRef?: React.RefObject<HTMLDivElement>,
  matchTriggerWidth = false,
) => {
  
  if (matchTriggerWidth || width === 'match-trigger') {
    return `${targetRef?.current?.getBoundingClientRect().width}px`;
  }

  return width;
};


